import {
  type FC,
  type PropsWithChildren,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { getDecimalSeparator, uses12HourFormat } from '@hub/prose-utils';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { useTranslation } from 'react-i18next';
import { selectOptionalUser } from 'features/auth/store/authSelectors';
import {
  getDateLocales,
  getDefaultLocale,
} from 'features/i18n/DateLocalization/getDateLocales';
import { getAppLocale } from 'features/i18n/utils/getAppLocale';
import { useReportLoading } from 'features/loading';
import { logger } from 'features/logger/logger';
import { useAppSelector } from 'features/store/hooks';
import { AppLocalizationContext } from './AppLocalizationContext';
import type { AppLocalizationContextValue } from './AppLocalizationContext';

export const AppLocalizationProvider: FC<PropsWithChildren> = ({
  children,
}) => {
  const user = useAppSelector(selectOptionalUser);
  const { i18n } = useTranslation();
  const [isSetup, setIsSetup] = useState(false);
  const [dateLocales, setDateLocales] = useState(getDefaultLocale());

  useEffect(() => {
    if (user?.language) {
      i18n.changeLanguage(user.language).catch(logger.error);
    }
  }, [i18n, user?.language]);

  useEffect(() => {
    async function loadDateLocales() {
      const locale = getAppLocale(i18n.language);
      const dateLocales = await getDateLocales(locale);

      setDateLocales(dateLocales);
      setIsSetup(true);
    }
    void loadDateLocales();
  }, [i18n.language]);

  const value: AppLocalizationContextValue = useMemo(() => {
    let decimal = '.';
    try {
      decimal = getDecimalSeparator(i18n.language);
    } catch (error) {
      logger.error(
        `Unable to determine the decimal separator for: ${i18n.language}`,
        undefined,
        error as Error
      );
    }

    let use12HourClock = false;
    try {
      use12HourClock = uses12HourFormat(i18n.language);
    } catch (error) {
      logger.error(
        `Unable to determine hour cycle for: ${i18n.language}`,
        undefined,
        error as Error
      );
    }

    return {
      appDateLocales: dateLocales,
      decimalSeparator: decimal,
      use12HourClock,
    };
  }, [i18n.language, dateLocales]);

  useReportLoading(!isSetup);

  return (
    <AppLocalizationContext.Provider value={value}>
      <LocalizationProvider
        dateAdapter={AdapterDateFns}
        adapterLocale={dateLocales.dateFns}
      >
        {isSetup ? children : null}
      </LocalizationProvider>
    </AppLocalizationContext.Provider>
  );
};
